import { useForm } from 'react-hook-form';
import useSaveSimulationPricingsSelfWorkerPricesMutation from './useSaveSimulationPricingsSelfWorkerPricesMutation';
import { useProcess } from '@fasstech/oip-starter-utils/app';
import {
  assoc,
  assocPath,
  compose,
  concat,
  dissoc,
  equals,
  evolve,
  filter,
  find,
  flatten,
  is,
  isEmpty,
  isNil,
  map,
  omit,
  pick,
  pluck,
  prop,
  propEq,
  propOr,
  propSatisfies,
  reject,
  split,
  toLower,
  uniq,
  values
} from 'ramda';
import { dateTsToSystem, tsToDateObj } from '@@viewHelpers/date.js';
import useGetSimulationPricingsSelfWorkerPricesQuery from './useGetSimulationPricingsSelfWorkerPricesQuery';
import { useEffect, useRef, useState } from 'react';
import { isNilOrEmpty } from '@@serverHelpers';
import { isNotNilOrEmpty } from '@@serverHelpers/index.mjs';
import { calculateHealthInsurances } from './helpers/calculateHealthInsurances';

const formatData = (guarantees) => {
  let formattedGuarantees = [];
  //Remove code index from data structure and useless options
  Object.keys(guarantees).forEach((key) => {
    formattedGuarantees[key] = {
      ...guarantees[key],
      options: null,
      _healthInsurance: undefined,
      _RO: undefined,
      _labelsRO: undefined
    };
  });
  formattedGuarantees = Object.values(formattedGuarantees);

  return formattedGuarantees;
};

const generateExternalId = (type, index) => `${type}_${index}`;

const SimulationPricingsSelfWorkerForm = ({
  getSimulationPricingsSelfWorker,
  commissions
}) => {
  const [loading, setLoading] = useState(false);
  const { onFormSubmit: saveSimulationPricingsSelfWorkerPricesMutation } =
    useSaveSimulationPricingsSelfWorkerPricesMutation();

  const [displayPriceError, setDisplayPriceError] = useState(false);
  const getComputedFranchiseDataRef = useRef(() => ({}));

  const flattenGuarantees = (guarantees) => {
    const result = {};

    const indexByCode = (item) => {
      result[item.code] = { ...item };
      if (item.options) {
        item.options.forEach(indexByCode);
      }
    };

    guarantees.forEach(indexByCode);
    return result;
  };

  const {
    processHandlers,
    context
  } = useProcess();

  const minBirthDateReducChild = dateTsToSystem(
    prop('minBirthDateReducChild', getSimulationPricingsSelfWorker)
  );
  const maxBirthDateReducChild = dateTsToSystem(
    prop('maxBirthDateReducChild', getSimulationPricingsSelfWorker)
  );
  const minBirthDateHospiChild = dateTsToSystem(
    prop('minBirthDateHospiChild', getSimulationPricingsSelfWorker)
  );
  const maxBirthDateHospiChild = dateTsToSystem(
    prop('maxBirthDateHospiChild', getSimulationPricingsSelfWorker)
  );

  const customerCSPCategory = prop('customerCSPCategory', getSimulationPricingsSelfWorker);

  const savedCommission = propOr({}, 'commission', getSimulationPricingsSelfWorker);

  const annualIncome = propOr(0, 'annualIncome', getSimulationPricingsSelfWorker);
  const PASS = propOr(0, 'PASS', getSimulationPricingsSelfWorker);

  const formattedGrantees = compose(
    map((grantee, index) =>
      compose(
        evolve({ birthDate: tsToDateObj }),
        assoc('editing', false),
        (g) => g.externalId
          ? g
          : assoc('externalId', generateExternalId(g.type, index), g),
        pick(['birthDate', 'type', 'externalId'])
      )(grantee)
    ),
    propOr([], 'grantees')
  )(getSimulationPricingsSelfWorker);

  const getCommissionOptions = compose(
    map(commission => ({
      label: commission.label,
      value: commission.code
    })),
    filter(
      compose(
        equals('oui'),
        toLower,
        propOr('non', 'isAllowed')
      )
    )
  );

  const formattedCommissions = map(commission => ({
    type: commission.code,
    rate: savedCommission.type === commission.code ? savedCommission.rate : commission.defaultCommissionRate
  }), commissions);

  const form = useForm({
    mode: 'onChange',
    reValidateMode: 'onChange',
    defaultValues: {
      guarantees: flattenGuarantees(getSimulationPricingsSelfWorker.guarantees),
      children: filter(propEq('type', 'CHILD'), formattedGrantees),
      spouse: find(propEq('type', 'SPOUSE'), formattedGrantees),
      commissions: formattedCommissions,
      commission: {
        rate: prop('rate', savedCommission),
        type: getCommissionOptions(commissions).length === 1 ? prop('type', formattedCommissions[0]) : prop('type', savedCommission)
      }
    }
  });

  const recommendationMessages = compose(
    uniq,
    filter(is(String)),
    reject(isNil),
    flatten,
    values,
    pluck('recommendedMessages')
  )(form.watch('guarantees'));

  const getCommissionRateOptions = compose(
    map(rate => ({
      label: rate.replace('/', ' / '),
      value: rate
    })),
    reject(isEmpty),
    split(';'),
    propOr('', 'commissionRate'),
    find(propEq('code', form.watch('commission.type')))
  );

  const gatherGrantees = (children, spouse) => {
    const childrenWithIds = children.map((child, index) => ({
      ...child,
      externalId: child.externalId || generateExternalId('CHILD', index)
    }));

    const spouseWithId = spouse?.birthDate
      ? [{
        ...spouse,
        externalId: spouse.externalId || generateExternalId('SPOUSE', 0)
      }]
      : [];

    return compose(
      reject(isNil),
      map(dissoc('editing'))
    )(concat(childrenWithIds, spouseWithId));
  };

  const guarantees = reject(propSatisfies(isNil, 'code'), form.watch('guarantees'));
  const guaranteesWithoutPrices = map(omit(['annualPrice', 'monthlyPrice', 'RO', 'healthInsurance', 'labelsRO', 'nodes']))(guarantees);

  const watchedCommission = form.watch('commission');
  const commissionType = prop('type', watchedCommission);
  const commissionRate = prop('rate', watchedCommission);

  useEffect(() => {
    if (isNotNilOrEmpty(commissionType)) {
      const selectedCommission = find(propEq('type', commissionType), form.getValues('commissions'));
      if (selectedCommission && selectedCommission.rate) {
        form.setValue('commission.rate', selectedCommission.rate);
      }
    }
  }, [commissionType]);

  const [isInvalDepCapFocused, setIsInvalDepCapFocused] = useState(false);
  const lastCommittedInvalDepCapRef = useRef(form.getValues('guarantees.INVAL_DEP_CAP.value'));

  const handleInvalDepCapFocus = () => {
    lastCommittedInvalDepCapRef.current = form.getValues('guarantees.INVAL_DEP_CAP.value');
    setIsInvalDepCapFocused(true);
  };

  const handleInvalDepCapBlur = () => {
    lastCommittedInvalDepCapRef.current = form.getValues('guarantees.INVAL_DEP_CAP.value');
    setIsInvalDepCapFocused(false);
  };

  const guaranteesForQuery = isInvalDepCapFocused && guaranteesWithoutPrices.INVAL_DEP_CAP
    ? assocPath(['INVAL_DEP_CAP', 'value'], lastCommittedInvalDepCapRef.current, guaranteesWithoutPrices)
    : guaranteesWithoutPrices;

  const {
    getSimulationPricingsSelfWorkerPrices: prices,
    loading: pricesLoading
  } =
    useGetSimulationPricingsSelfWorkerPricesQuery({
      variables: {
        grantees: gatherGrantees(form.watch('children'), form.watch('spouse')),
        guarantees: formatData(guaranteesForQuery),
        commission: {
          type: commissionType,
          rate: commissionRate
        }
      }
    });

  const guaranteesWithPrices = prices.guarantees;

  useEffect(() => {
    if (pricesLoading) return;
    if (isNilOrEmpty(guaranteesWithPrices)) {
      setDisplayPriceError(true);
    } else {
      setDisplayPriceError(false);
      for (const guarantee of guaranteesWithPrices) {
        form.setValue(`guarantees.${guarantee.code}.annualPrice`, guarantee.annualPrice);
        form.setValue(`guarantees.${guarantee.code}.monthlyPrice`, guarantee.monthlyPrice);
        form.setValue(`guarantees.${guarantee.code}.RO`, guarantee.RO);

        const currentLabelsRO = form.getValues(`guarantees.${guarantee.code}.labelsRO`);
        if (guarantee.labelsRO && guarantee.labelsRO.length > 0) {
          form.setValue(`guarantees.${guarantee.code}.labelsRO`, guarantee.labelsRO);
        } else if (!currentLabelsRO || currentLabelsRO.length === 0) {
          form.setValue(`guarantees.${guarantee.code}.labelsRO`, guarantee.labelsRO);
        }
      }
    }
  }, [guaranteesWithPrices, pricesLoading]);

  const onSubmit = (values) => {
    if (loading) return;
    setLoading(true);

    const computedFranchiseData = getComputedFranchiseDataRef.current();
    const guaranteesWithHealthInsurances = calculateHealthInsurances(
      values.guarantees,
      annualIncome,
      computedFranchiseData
    );
    values.guarantees = formatData(guaranteesWithHealthInsurances);

    const formattedValues = compose(
      dissoc('spouse'),
      dissoc('children'),
      dissoc('commissions'),
      assoc('grantees', gatherGrantees(values.children, values.spouse)),
      assocPath(['commission', 'rate'], compose(
        propOr('', 'rate'),
        find(propEq('type', values.commission.type))
      )(values.commissions))
    )(values);

    saveSimulationPricingsSelfWorkerPricesMutation(formattedValues, (ok, error) => {
      if (ok && !error) {
        processHandlers.onNext({
          context: {
            projectId: context?.projectId
          }
        });
      } else {
        setLoading(false);
      }
    });
  };

  const flattenedPricesGuarantees = prices?.guarantees ? flattenGuarantees(prices.guarantees) : {};

  const registerComputedFranchiseDataGetter = (getterFn) => {
    getComputedFranchiseDataRef.current = getterFn;
  };

  return {
    form,
    onSubmit,
    guarantees,
    pricesGuarantees: flattenedPricesGuarantees,
    displayPriceError,
    minBirthDateReducChild,
    maxBirthDateReducChild,
    minBirthDateHospiChild,
    maxBirthDateHospiChild,
    customerCSPCategory,
    getCommissionRateOptions,
    getCommissionOptions,
    handleInvalDepCapFocus,
    handleInvalDepCapBlur,
    recommendationMessages,
    annualIncome,
    PASS,
    registerComputedFranchiseDataGetter
  };
};

export default SimulationPricingsSelfWorkerForm;
